define([
  'react',
  'underscore',
  'es6!launcher/util/listbuilder/constants',
], (React, _, Constants) => {
  const { FormTypes, APP_ID, SUMO_BADGE_HEIGHT_VAL, SUMO_BADGE_PLACEMENT_VAL } = Constants;

  // Render the main Sumo badge logo component for non-pro popup branding
  class SumoBadge extends React.Component {
    constructor(props) {
      super(props);

      this.state = {
        loading: true
      };

      this.onClickPoweredBy = this.onClickPoweredBy.bind(this);
      this.getScrollBoxBadgePlacement = this.getScrollBoxBadgePlacement.bind(this);
      this.onMountBadgeRef = this.onMountBadgeRef.bind(this);

      // Define badge placement position; convert integer value to css px string.
      // Negative value as badge is absolutely positioned outside of popup container.
      this.badgePlacementStyle = '-' + SUMO_BADGE_PLACEMENT_VAL.toString() + 'px';
    }

    componentDidMount() {
      const { isPreview } = this.props;

      // Delay badge display for 1.4 seconds to grab attention, only if not in preview (DesignView) mode
      if (!isPreview) {
        setTimeout(() => {
          this.setState({
            loading: false,
          });
        }, 1400);
      } else {
        this.setState({ loading: false });
      }
    }

    // Record new badge clicks with id 56696fd...
    onClickPoweredBy() {
      const { api, isPreview } = this.props;

      if (!isPreview) {
        const powerVariants = ['56696fd2-9edf-473f-9aba-a952a5723fb7'];
        const powerVariant = powerVariants[0];
        const refLink = `https://sumome.com/?src=sm-lb-${api.properties.siteId}&utm_source=sumo-powered-by&utm_medium=list-builder&utm_campaign=${api.properties.siteId}`;
        const event = `powered-by-click:${APP_ID}:${powerVariant}`;

        api.emit('event', { 'appId': '2d1fffc9-378f-4b61-ac79-2dc8a077b827', 'event': event });
        window.open(refLink);
      }
    }

    // Determine scroll box badge placement styling based on showPosition property ('bottom-right' etc)
    // Note: this property is not relevant to regular popups, click-its and inline popups
    getScrollBoxBadgePlacement(showPosition, shouldHideScrollBoxBadge) {
      let placement = '';

      // Get 'bottom' or 'top' placement property
      showPosition = showPosition.split('-')[0];

      if (shouldHideScrollBoxBadge) {
        placement = 'display: none !important;';
      } else if (showPosition === 'top') {
        placement = `bottom: ${this.badgePlacementStyle} !important;`;
      } else {
        placement = `top: ${this.badgePlacementStyle} !important;`;
      }

      return placement;
    }

    // Set scoping for anchor element so we can use in updateBadgeStyles; force update once to add styles
    onMountBadgeRef(anchorEl) {
      if (!anchorEl) return;
      this.poweredByAnchor = anchorEl;

      setTimeout(() => {
        this.forceUpdate();
      });
    }

    // Set inline !important styles for inner-badge logo element to thwart crafty devs
    onMountLogoRef(logoEl) {
      if (!logoEl) return;

      const inlineStyles = 'display: block !important; visibility: visible !important; opacity: 1 !important;';
      logoEl.setAttribute('style', inlineStyles);
    }

    // Set inline !important styles for main badge anchor element to thwart crafty devs
    updateBadgeStyles(scale, placement) {
      if (this.poweredByAnchor && placement) {
        const inlineStyles = `position: absolute !important; display: block !important; visibility: visible !important; opacity: 1 !important; z-index: 99 !important; ${scale} ${placement}`;
        this.poweredByAnchor.setAttribute('style', inlineStyles);
      }
    }

    render() {
      /* isPreview & loading determine whether or not badge will be rendered.
       * isPreview indicates whether or not popup is currently being displayed in DesignView (do not show)
       * formType is used along with FormTypes Constants to determine type of popup for badge placement.
       * showPosition is used to determine specific positioning rules for scroll box popups.
       */
      const { api, formType, height, isPreview, scaleRatio, showPosition } = this.props;
      const { loading } = this.state;
      // Shopify does not allow bdow to self promote within a shopify plugin
      // TODO: move this logic to the server
      const isExemptPlatform = api?.properties?.isShopifyBilling;

      // Do not show sumo badge if popup is being displayed in DesignView, or if component is still loading
      if (loading || isPreview || isExemptPlatform) return null;

      let popupHeight = height;

      /* Keep original top/bottom position placement of badge after scaling with transform-origin.
       * Scroll Box top/bottom will == position; all other forms need to maintain badge top origin
       * Note: showPosition will be undefined for advanced forms
       */
      let position = '';

      if ((showPosition && formType === FormTypes.SCROLL_BOX)) {
        // Outcomes === 'top' or 'bottom'
        position = showPosition.split('-')[0];
      } else {
        position = 'top';
      }

      // Determine badge scale adjustment based on scaleRatio.
      // Apply inverse if popup scaleRatio > 1 to retain original badge size.
      let scale = '';

      if (scaleRatio > 1) {
        scale = 1/scaleRatio;
        scale = `transform: translateX(-50%) scale(${scale}, ${scale}) !important; transformOrigin: ${position} !important;`;
      }

      /* Get window height so badge cutoff is prevented for full-screen or near full-screen scroll boxes.
       * Calculate percentages based off of height and scale values.
       * If ratios > 100%, hide the badge.
       */
      popupHeight = (scaleRatio > 1) ? (popupHeight * scaleRatio) : popupHeight;

      const screenHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      let badgeBufferRatio = (SUMO_BADGE_HEIGHT_VAL + SUMO_BADGE_PLACEMENT_VAL) / screenHeight;
      let popupHeightRatio = popupHeight / screenHeight;
      const shouldHideScrollBoxBadge = (badgeBufferRatio + popupHeightRatio) > 1;

      /* Badge positioning guide:
       * Badge is absolutely positioned inside of popup and will remain close to popup container
       * Click It and Popups:
       *  -Bottom-centered
       * Scrollbox:
       *  -Top left or top right: bottom-centered position
       *  -Bottom left or bottom right: top-centered position
       *  -If scroll box is greater than 73% of the window height, badge will be hidden to prevent overflow.
       * Inline (embedded):
       *  -Bottom-centered position
       */
      let placement = '';

      if (formType === FormTypes.SCROLL_BOX) {
        placement = this.getScrollBoxBadgePlacement(showPosition, shouldHideScrollBoxBadge);
      } else {
        placement = `bottom: ${this.badgePlacementStyle} !important;`;
      }

      // Define inner svg styles
      const st0 = {
        fill: '#0F74B5',
      };

      const st1 = {
        opacity: .2,
        fill: '#041D2D',
      };

      const st2 = {
        fill: '#000000',
      };

      this.updateBadgeStyles(scale, placement);

      return (
        <div>
          <a ref={this.onMountBadgeRef} className="powered-by-sumo" onClick={this.onClickPoweredBy}>
            <p>POWERED BY</p>
            <div ref={this.onMountLogoRef} className="sumo-logo">
              <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 230 280">
                <path style={{fill: '#fecc4c'}} d="m150.04,123.95c-9.93-7.92,5.59-55.8,5.59-55.8,0,0-33.65,29.74-47.33,27.59-16.41-2.57-36.13-37.32-36.13-37.32v158.9l126.85-96.56s-39.04,11.12-48.97,3.2Z"/>
                <path style={{fill: '#e35225'}} d="m106.16,211.09l3-.42s1.22-33.05,1.76-47.37l1.4-40.93-26.4-26.4-29.72,4.21,3.2,88.97,4.32,4.32-6.22.87v24.62l26.08,26.08,27.53-3.85-.06-25.2-4.9-4.9v-.02Z"/>
                <path style={{fill: '#fbbfad'}} d="m84.38,100.14l-24.43,3.49c.99,27.64,2.05,56.79,3.04,84.55l18.34-2.59,3.04-85.46Z"/>
                <path style={{fill: '#fbbfad'}} d="m82.29,193.87l-20.5,2.9,9.06,8.79,11.43-11.69Z"/>
                <path style={{fill: '#fbbfad'}} d="m71.94,208.13c-.3.3-.67.45-1.07.45s-.75-.14-1.03-.42l-8.75-8.47-.06,17.63,22.15-3.1-.04-17.57-11.24,11.49.02-.02Z"/>
              </svg>
            </div>
            <div className="clear"></div>
          </a>
        </div>
      );
    }
  }
  return SumoBadge;
});
